import { getIdToken } from './session';

const API_URL = import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function apiFetch<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  // Edge functions accept the Auth0 ID token directly (_shared/auth.ts).
  const token = await getIdToken();

  const fetchOptions: RequestInit = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  if (options.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_URL}/functions/v1/${endpoint}`, fetchOptions);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'API request failed');
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Conversation API
export interface Conversation {
  id: string;
  type: string;
  status: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  last_message?: {
    content: string;
    sender_type: string;
    created_at: string;
  };
  unread_count: number;
  csat_rating: number | null;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  sender_type: 'user' | 'agent' | 'ai_agent' | 'system';
  content: string | null;
  content_type: string;
  ai_model: string | null;
  ai_confidence: number | null;
  ai_citations: Array<{ source_id: string; title: string; section: string | null }> | null;
  attachments: Array<{ url: string; filename: string; mimeType: string; size: number }>;
  created_at: string;
  edited_at: string | null;
  sender?: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
    role: string;
  };
}

export const conversationApi = {
  async list(status = 'open'): Promise<{ conversations: Conversation[] }> {
    return apiFetch(`conversations?status=${status}`);
  },

  async get(id: string): Promise<Conversation> {
    return apiFetch(`conversations/${id}`);
  },

  async create(data: {
    type: string;
    title?: string;
    initialMessage?: string;
  }): Promise<Conversation> {
    return apiFetch('conversations', {
      method: 'POST',
      body: data,
    });
  },

  async update(
    id: string,
    data: Partial<Conversation>
  ): Promise<Conversation> {
    return apiFetch(`conversations/${id}`, {
      method: 'PUT',
      body: data,
    });
  },
};

export const messageApi = {
  async list(
    conversationId: string,
    options: { limit?: number; before?: string } = {}
  ): Promise<{ messages: Message[]; has_more: boolean }> {
    const params = new URLSearchParams({
      conversationId,
      ...(options.limit && { limit: options.limit.toString() }),
      ...(options.before && { before: options.before }),
    });
    return apiFetch(`messages?${params}`);
  },

  async send(data: {
    conversationId: string;
    content: string;
    contentType?: string;
  }): Promise<Message> {
    return apiFetch('messages', {
      method: 'POST',
      body: data,
    });
  },
};

// Help-center article admin API.
// Reads live on the anon-readable help-center function; writes go through
// help-center-admin which chunks + embeds content so articles show up in
// hybrid_search the moment they are saved.
export interface ArticleInput {
  title?: string;
  description?: string | null;
  category?: string | null;
  content?: string;
  isPublished?: boolean;
  accessLevel?: 'public' | 'internal' | 'restricted';
  displayOrder?: number;
}

export interface ArticleSaveResult {
  id: string;
  chunkCount: number | null;
}

export const articleApi = {
  async create(
    data: ArticleInput & { title: string; content: string }
  ): Promise<ArticleSaveResult> {
    return apiFetch('help-center-admin', {
      method: 'POST',
      body: data,
    });
  },

  async update(id: string, data: ArticleInput): Promise<ArticleSaveResult> {
    return apiFetch(`help-center-admin?id=${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: data,
    });
  },

  async remove(id: string): Promise<{ success: boolean }> {
    return apiFetch(`help-center-admin?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};
